import {
  AbstractSigner,
  type BlockTag,
  defineProperties,
  type Provider,
  type Signer,
  type TransactionRequest,
  type TransactionResponse,
  type TypedDataDomain,
  type TypedDataField,
} from 'ethers';

/**
 *  A **NonceManager** wraps another [[Signer]] and automatically manages
 *  the nonce, ensuring serialized and sequential nonces are used during
 *  transaction.
 */

// getNonce was changed as gaps are not considered in besu
export class _NonceManager extends AbstractSigner {
  /**
   *  The Signer being managed.
   */
  signer!: Signer;

  #noncePromise: null | Promise<number>;
  #delta: number;

  /**
   *  Creates a new **NonceManager** to manage %%signer%%.
   */
  constructor(signer: Signer) {
    super(signer.provider);
    defineProperties<_NonceManager>(this, { signer });

    this.#noncePromise = null;
    this.#delta = 0;
  }

  async getAddress(): Promise<string> {
    return this.signer.getAddress();
  }

  connect(provider: null | Provider): _NonceManager {
    return new _NonceManager(this.signer.connect(provider));
  }

  async getNonce(blockTag?: BlockTag): Promise<number> {
    if (blockTag === 'pending') {
      if (this.#noncePromise == null) {
        this.#noncePromise = super.getNonce(); // only for besu as when using "pending" gaps are not considered
      }

      const delta = this.#delta;
      return (await this.#noncePromise) + delta;
    }

    return super.getNonce(blockTag);
  }

  /**
   *  Manually increment the nonce. This may be useful when managng
   *  offline transactions.
   */
  increment(): void {
    this.#delta++;
  }

  /**
   *  Resets the nonce, causing the **NonceManager** to reload the current
   *  nonce from the blockchain on the next transaction.
   */
  reset(): void {
    this.#delta = 0;
    this.#noncePromise = null;
  }

  async sendTransaction(tx: TransactionRequest): Promise<TransactionResponse> {
    tx = await this.signer.populateTransaction(tx);

    // this is different from the original class NonceManager
    // as populateTransaction can throw an error
    // so we don't want to increment the nonce
    const noncePromise = this.getNonce('pending');
    this.increment();

    tx.nonce = await noncePromise;

    // @TODO: Maybe handle interesting/recoverable errors?
    // Like don't increment if the tx was certainly not sent
    return await this.signer.sendTransaction(tx);
  }

  signTransaction(tx: TransactionRequest): Promise<string> {
    return this.signer.signTransaction(tx);
  }

  signMessage(message: string | Uint8Array): Promise<string> {
    return this.signer.signMessage(message);
  }

  signTypedData(domain: TypedDataDomain, types: Record<string, Array<TypedDataField>>, value: Record<string, any>): Promise<string> {
    return this.signer.signTypedData(domain, types, value);
  }
}
